import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { Typography, darken, InputAdornment, TextField, Grid, ClickAwayListener, lighten } from '@mui/material';
import PolarisIcon from '../PolarisIcon';
import useDebounce from '../../_hooks/useDebounce';
import axios from '@/_utils/axios';
import { TitleEntity } from '@/redux/slices/slice_types';
import TitleTypeLabel from '../TitleTypeLabel';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { getLineageMetaTitle } from '@/_utils/utils';

interface TitleSearchProps {
  onTitleSelect: (title: TitleEntity) => void;
}

const TitleSearch = ({ onTitleSelect }: TitleSearchProps) => {
  const [results, setResults] = React.useState<TitleEntity[]>([]);
  const [searchInput, setSearchInput] = React.useState<string>('');
  const debouncedSearchValue = useDebounce(searchInput, 500);
  const titleAssociationEntityTypes = useConfigValue(['dataConfig', 'fileIngest', 'titleAssociation', 'entity_types_filter']);
  const prettyTitleDisplay = useConfigValue(['dataConfig', 'fileIngest', 'titleAssociation', 'prettyTitleDisplay']);
  const opEntityTypes = useConfigValue(['orderPlacementConfig', 'entityTypes']);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  useEffect(() => {
    if (!debouncedSearchValue) {
      setResults([]);
      return;
    }
    axios
      .post(`/inventory/titles/search/`, {
        search: debouncedSearchValue,
        entity_types_filter: titleAssociationEntityTypes || opEntityTypes || ['feature', 'series']
      })
      .then((res) => {
        setResults(res.data.data);
      });
  }, [debouncedSearchValue]);

  const getTitleName = (title: TitleEntity) => {
    if (prettyTitleDisplay) {
      return title.lineage_meta?.series_name || title.lineage_meta?.feature_name || title.name;
    }
    return title.name;
  }

  const onSelect = (title: TitleEntity) => {
    const lineageMetaTitle = getLineageMetaTitle(title);
    const name = lineageMetaTitle?.trim() ? lineageMetaTitle : getTitleName(title);

    onTitleSelect({
      ...title,
      name: prettyTitleDisplay ? name : title.name,
    });
    setSearchInput('');
    setResults([]);
  };

  const renderTitle = (result: TitleEntity) => {
    if (prettyTitleDisplay) {
      return (
        <div>
          <strong>{getTitleName(result)}</strong>
          <div>
            <Typography variant="caption">{getLineageMetaTitle(result)}</Typography>
          </div>
        </div>

      );
    }

    const hasChildren = result.children?.length;
    return (
      <Typography
        component="span"
        sx={{ fontSize: '1.2rem', fontStyle: hasChildren ? 'normal' : 'italic', opacity: hasChildren ? 1 : 0.4 }}
      >
        <TitleTypeLabel type={result.entity_type} small width="5rem" /> {result.name}
      </Typography>
    );
  }

  const renderChildrenCount = (result: TitleEntity) => {
    if (prettyTitleDisplay) {
      return result.external_id;
    }
    const childCount = result.children?.length;
    if (!childCount) {
      return <em style={{ opacity: 0.5 }}>No children</em>;
    } else {
      return `${childCount} child${childCount > 1 ? 'ren' : ''}`;
    }
  }

  return (
    <ClickAwayListener onClickAway={() => setResults([])}>
      <div style={{ position: 'relative' }}>
        <StyledSearch
          placeholder="Search for a title"
          variant="filled"
          hiddenLabel
          value={searchInput}
          onChange={handleOnChange}
          autoComplete="off"
          hasresults={+(results.length > 0)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PolarisIcon icon="search" height={28} />
              </InputAdornment>
            )
          }}
        />
        <ResultsContainer hasresults={+(results.length > 0 || searchInput.length > 0)}>
          {results.length > 0
            ? results.map((result) => {
                return (
                  <Result key={result.id} onClick={() => onSelect(result)}>
                    <Grid container alignItems="center" justifyContent="space-between">
                      {renderTitle(result)}
                      <Typography sx={{ fontSize: '.9rem' }}>
                        <span style={{ minWidth: 70, display: 'inline-block', opacity: 0.6 }}>
                          {renderChildrenCount(result)}
                        </span>
                      </Typography>
                    </Grid>
                  </Result>
                );
              })
            : searchInput.length > 0 && (
                <NoResults>
                  <Typography>No titles found.</Typography>
                </NoResults>
              )}
        </ResultsContainer>
      </div>
    </ClickAwayListener>
  );
};

export default TitleSearch;

const StyledSearch = styled(TextField)<{ hasresults: number }>`
  width: 100%;
  .MuiInputBase-root {
    font-size: 1.2rem;
    border: 1px solid transparent;
    border-bottom: 0;
    ${({ hasresults, theme }) =>
      hasresults &&
      `
        background: ${darken(theme.palette.background.paper, 0.3)};
        border-color: ${theme.palette.divider};
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    `}
  }
  .MuiInputAdornment-root {
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const ResultsContainer = styled.div<{ hasresults: number }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => darken(theme.palette.background.paper, 0.3)};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-top: none;
  border-radius: 0 0 4px 4px;
  z-index: 10;
  max-height: 300px;
  overflow-y: auto;
  opacity: 0;
  transition: opacity 0.3s;

  ${({ hasresults }) =>
    hasresults &&
    `
  transition: opacity 0.3s;
    opacity: 1;
  `}
`;

const Result = styled.div`
  padding: ${({ theme }) => theme.spacing(1.25, 3)};
  color: ${({ theme }) => theme.palette.text.primary};
  transition: color 0.3s;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => lighten(theme.palette.background.paper, 0.05)};
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const NoResults = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.palette.text.secondary};
`;
